import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import theme from './constants/theme.js';
import PrivacyPolicy from './screens/PrivacyPolicy.jsx';
import SplashPage from './screens/SplashPage.jsx';
import CondensedCenterContainer from './components/layout/CondensedCenterContainer.jsx';
import VerificationForm from './screens/VerificationForm.jsx';
import StudentRepository from './services/StudentRepository.js';
import CivilStatusForm from './screens/CivilStatus.jsx';
import Examinations from './screens/Examinations.jsx';
import Address from './screens/Address.jsx';
import Contact from './screens/Contact.jsx';
import Employment from './screens/Employment.jsx';
import ProgramSynthesis from './screens/ProgramSynthesis.jsx';
import Epilogue from './screens/Epilogue.jsx';
import CollegeQuestions from './screens/CollegeQuestions.jsx';
import TwoYearStatus from './screens/TwoYearStatus.jsx';
import TransitionScreen from './screens/TransitionScreen.jsx';
import EmploymentStatusScreen from './screens/EmploymentStatusScreen.jsx';
import StepperComponent from './components/common/StepperComponent.jsx';
import screenCategorizer from './utils/ScreenCategorizer.js';

function App({ baseUrl }) {
  const [activeScreen, setActiveScreen] = React.useState(0);
  const [slideIn, setSlideIn] = React.useState(true);
  const [slideDirection, setSlideDirection] = React.useState('up');
  const [alertElement, setAlertElement] = React.useState(null);
  const [studentCollege, setStudentCollege] = React.useState(null);
  const [formValues, setFormValues] = React.useState({
    civil_status: null,
    gender: null,
    married_name: null,
    pa_unit: null,
    pa_street: null,
    pa_brgy: null,
    pa_city: null,
    pa_province: null,
    pa_country: null,
    ca_unit: null,
    ca_street: null,
    ca_brgy: null,
    ca_city: null,
    ca_province: null,
    ca_country: null,
    is_first_gen_graduate: null,
    email: null,
    contact_number: null,
    social_media: null,
    months_before_first_job: null,
    competencies: [],
    suggestions: null,
    is_employed_two_yrs_postgrad: null,
    is_studying_two_yrs_postgrad: null,
    employment_status: null,
    job_course_alignment: null,
  });
  const collegesWithQuestions = {
    CED: ProgramSynthesis,
  };
  const handleScreenNext = (iterator = 1) => {
    handleErrors(null);
    const iterable = activeScreen < SCREENS.length - iterator;
    if (!iterable) return;
    setSlideDirection('right');
    setSlideIn(false);
    setTimeout(() => {
      setActiveScreen(() => activeScreen + iterator);
      setSlideDirection('left');
      setSlideIn(true);
    }, 300);
  };
  const handleScreenNextWithCollege = () => {
    if (!(studentCollege in collegesWithQuestions)) return handleScreenNext(2);
    return handleScreenNext();
  };

  const handleScreenBack = (iterator = 1) => {
    handleErrors(null);
    const iterable = activeScreen > 0;
    if (!iterable) return;
    setSlideDirection('left');
    setSlideIn(false);
    setTimeout(() => {
      setActiveScreen(() => activeScreen - iterator);
      setSlideDirection('right');
      setSlideIn(true);
    }, 300);
  };
  // const handleScreenBackWithCollege = () => {
  //   if (!(studentCollege in collegesWithQuestions)) return handleScreenBack(2);
  //   return handleScreenBack();
  // };
  const handleScreenShiftNext = () => {
    handleErrors(null);
    const iterable = activeScreen < SCREENS.length - 1;
    if (!iterable) return;
    setSlideDirection('down');
    setSlideIn(false);
    setTimeout(() => {
      setActiveScreen(() => activeScreen + 1);
      setSlideDirection('up');
      setSlideIn(true);
    }, 300);
  };
  const handleScreenShiftBack = () => {
    handleErrors(null);
    const iterable = activeScreen > 0;
    if (!iterable) return;
    setSlideDirection('up');
    setSlideIn(false);
    setTimeout(() => {
      setActiveScreen(() => activeScreen - 1);
      setSlideDirection('down');
      setSlideIn(true);
    }, 300);
  };

  const handleSkipToEpilogue = () => {
    if (
      !window.confirm(
        'Are you sure you want to skip the remaining questions and finish the survey?'
      )
    ) {
      return;
    }
    handleErrors(null);
    setSlideDirection('right');
    setSlideIn(false);
    setTimeout(() => {
      setActiveScreen(() => SCREENS.length - 1); // Jump to Epilogue
      setSlideDirection('left');
      setSlideIn(true);
    }, 300);
  };
  const handleErrors = (alertElement) => {
    setAlertElement(alertElement);
  };
  const goToMainSite = () => {
    window.location.href = 'https://www.msuiit.edu.ph';
    return null;
  };
  const studentRepo = new StudentRepository(baseUrl);
  const getOnSkip = (screenIndex) => {
    const category = screenCategorizer.getCategoryForScreen(screenIndex);
    return category?.isOptional ? handleSkipToEpilogue : undefined;
  };

  const SCREENS = [
    <SplashPage key="screen_i" handleScreenNext={handleScreenShiftNext} />,
    <PrivacyPolicy
      key="screen_ii"
      handleScreenNext={handleScreenShiftNext}
      handleScreenBack={goToMainSite}
    />,
    <VerificationForm
      repo={studentRepo}
      key="screen_1"
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenShiftBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      setStudentCollege={setStudentCollege}
      onSkip={getOnSkip(2)}
    />,
    <CivilStatusForm
      repo={studentRepo}
      key="screen_2"
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(3)}
    />,
    <Address
      repo={studentRepo}
      key="screen_3"
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(4)}
    />,
    <Contact
      repo={studentRepo}
      key="screen_4"
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(5)}
    />,
    <EmploymentStatusScreen
      key="screen_emp_status"
      repo={studentRepo}
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(6)}
    />,
    <TransitionScreen
      key="screen_transition"
      onBack={handleScreenBack}
      onContinue={handleScreenNext}
      onSkip={getOnSkip(7)}
    />,
    <Examinations
      key="screen_5"
      repo={studentRepo}
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      // handleErrors={handleErrors}
      formValues={formValues}
      // setFormValues={setFormValues}
      onSkip={getOnSkip(8)}
    />,
    <TwoYearStatus
      key="screen_6"
      repo={studentRepo}
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(9)}
    />,
    <Employment
      key="screen_7"
      repo={studentRepo}
      handleScreenNext={handleScreenNextWithCollege}
      handleScreenBack={handleScreenBack}
      handleErrors={handleErrors}
      formValues={formValues}
      setFormValues={setFormValues}
      onSkip={getOnSkip(10)}
    />,
    <CollegeQuestions
      key="screen_8"
      repo={studentRepo}
      handleScreenNext={handleScreenNext}
      handleScreenBack={handleScreenBack}
      formValues={formValues}
      setFormValues={setFormValues}
      college={studentCollege}
      collegeMap={collegesWithQuestions}
      onSkip={getOnSkip(11)}
    />,
    <Epilogue repo={studentRepo} key="screen_9" />,
  ];
  const brandingPlacement = () => {
    return activeScreen > 1 && activeScreen < SCREENS.length - 1 ? 'top' : 'centered';
  };

  const currentCategory = screenCategorizer.getCurrentCategory(activeScreen);
  const categories = screenCategorizer.getCategories();

  const steps = categories.map((category) => ({
    label: category.displayName,
    isOptional: category.isOptional,
    isCompleted: screenCategorizer.isCategoryCompleted(category.id, activeScreen),
    isActive: currentCategory && currentCategory.id === category.id,
  }));

  const currentStepIndex = currentCategory
    ? screenCategorizer.getCategoryIndex(currentCategory.id)
    : 0;

  const showStepper = activeScreen > 1 && activeScreen < SCREENS.length - 1;

  const stepper = showStepper ? (
    <StepperComponent steps={steps} currentStep={currentStepIndex} />
  ) : null;
  return (
    <ThemeProvider theme={theme}>
      <CondensedCenterContainer
        slideIn={slideIn}
        direction={slideDirection}
        brandingPlacement={brandingPlacement()}
        error={alertElement}
        stepper={stepper}
      >
        {SCREENS[activeScreen]}
      </CondensedCenterContainer>
    </ThemeProvider>
  );
}

App.propTypes = {
  baseUrl: PropTypes.string,
};

export default App;
